import mapboxgl from 'mapbox-gl';

import { UnitConversions } from '../../general-utilities/numbers-util';

class CustomScaleControl implements mapboxgl.IControl {
  private container: HTMLDivElement | null = null;
  private mapInstance: mapboxgl.Map | null = null;
  private scaleBar: HTMLDivElement | null = null;
  private label: HTMLDivElement | null = null;
  private updateScaleHandler: (() => void) | null = null;

  onAdd(map: mapboxgl.Map): HTMLElement {
    this.mapInstance = map;
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-scale';
    this.container.style.cssText = `
      background: transparent;
      border: none;
      box-shadow: none;
      padding: 0;
      margin: 0;
    `;

    // Create label for distance text
    this.label = document.createElement('div');
    this.label.style.cssText = `
      color: #fff;
      font-size: 0.75rem;
      font-weight: 400;
      text-align: center;
      text-shadow: 
        -1px -1px 0 #000,
        1px -1px 0 #000,
        -1px 1px 0 #000,
        1px 1px 0 #000,
        0 0 2px #000;
      margin-bottom: 2px;
      white-space: nowrap;
    `;

    const scaleBarContainer = document.createElement('div');
    scaleBarContainer.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    `;

    const leftLine = document.createElement('div');
    leftLine.style.cssText = `
      width: 2px;
      height: 8px;
      background-color: #fff;
      flex-shrink: 0;
    `;

    this.scaleBar = document.createElement('div');
    this.scaleBar.style.cssText = `
      height: 2px;
      background-color: #fff;
      flex-shrink: 0;
    `;

    const rightLine = document.createElement('div');
    rightLine.style.cssText = `
      width: 2px;
      height: 8px;
      background-color: #fff;
      flex-shrink: 0;
    `;

    scaleBarContainer.appendChild(leftLine);
    scaleBarContainer.appendChild(this.scaleBar);
    scaleBarContainer.appendChild(rightLine);

    this.container.appendChild(this.label);
    this.container.appendChild(scaleBarContainer);

    // Update scale on map move/zoom
    this.updateScaleHandler = () => {
      if (!this.mapInstance || !this.scaleBar || !this.label) return;

      const metersPerPixel = this.calculateMetersPerPixel();
      const widthInPixels = 85; // Base width for scale bar
      const meters = metersPerPixel * widthInPixels;
      const miles = meters / 1609.34; // Convert meters to miles

      // Use feet for small distances (< 0.5 miles), miles for larger distances
      let displayValue: number;
      let unit: string;

      if (miles < 0.5) {
        // Convert to feet for small distances
        const feet = UnitConversions.mToF(meters);
        displayValue = CustomScaleControl.roundToNiceNumberFt(feet);
        unit = displayValue === 1 ? 'foot' : 'feet';
      } else {
        // Use miles for larger distances
        displayValue = CustomScaleControl.roundToNiceNumberMiles(miles);
        unit = displayValue === 1 ? 'mile' : 'miles';
      }

      // Calculate the actual width based on the rounded value
      let actualWidth: number;
      if (unit === 'feet' || unit === 'foot') {
        const roundedMeters = displayValue / 3.28084; // Convert feet back to meters
        actualWidth = roundedMeters / metersPerPixel;
      } else {
        const roundedMeters = displayValue * 1609.34; // Convert miles back to meters
        actualWidth = roundedMeters / metersPerPixel;
      }

      this.scaleBar.style.width = `${actualWidth}px`;
      this.label.textContent = `${displayValue} ${unit}`;
    };

    map.on('move', this.updateScaleHandler);
    map.on('zoom', this.updateScaleHandler);
    this.updateScaleHandler(); // Initial update

    return this.container;
  }

  onRemove(): void {
    if (this.mapInstance && this.updateScaleHandler) {
      this.mapInstance.off('move', this.updateScaleHandler);
      this.mapInstance.off('zoom', this.updateScaleHandler);
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.mapInstance = null;
    this.container = null;
    this.scaleBar = null;
    this.label = null;
    this.updateScaleHandler = null;
  }

  // eslint-disable-next-line class-methods-use-this
  getDefaultPosition(): 'bottom-left' {
    return 'bottom-left';
  }

  private calculateMetersPerPixel(): number {
    if (!this.mapInstance) return 1;

    const center = this.mapInstance.getCenter();
    const zoom = this.mapInstance.getZoom();
    const latitude = center.lat;

    // Calculate meters per pixel based on zoom level and latitude
    const earthCircumference = 40075017; // meters at equator
    const metersPerPixel = (earthCircumference * Math.cos((latitude * Math.PI) / 180)) / (256 * 2 ** zoom);

    return metersPerPixel;
  }

  private static roundToNiceNumberFt(value: number): number {
    if (value <= 50) {
      // 50 or lower: round to nearest 10 ft (10, 20, 30, 40, 50)
      return Math.round(value / 10) * 10;
    }
    if (value < 100) {
      // Under 100: round to 50 or 100
      return value < 75 ? 50 : 100;
    }
    if (value <= 250) {
      // Between 100 and 250: increase by 50 ft (100, 150, 200, 250)
      return Math.round(value / 50) * 50;
    }
    if (value <= 1000) {
      // Between 250 and 1000: increase by 250 ft (250, 500, 750, 1000)
      return Math.round(value / 250) * 250;
    }
    if (value <= 4000) {
      // Between 1k and 4k feet: increase by 1k ft (1000, 2000, 3000, 4000)
      return Math.round(value / 1000) * 1000;
    }
    // Above 4000 feet, convert to miles (handled by caller)
    return Math.round(value / 1000) * 1000;
  }

  private static roundToNiceNumberMiles(value: number): number {
    if (value < 10) {
      // Under 10 miles: increase by 1 mile (1, 2, 3, ..., 9)
      return Math.round(value);
    }
    if (value < 25) {
      // Under 25 miles: increase by 5 miles (10, 15, 20, 25)
      return Math.round(value / 5) * 5;
    }
    if (value <= 100) {
      // Between 50-100 miles: increase by 50 miles (50, 100)
      // Note: values between 25-50 will round to 50
      if (value < 50) {
        return 50;
      }
      return Math.round(value / 50) * 50;
    }
    if (value <= 250) {
      // Between 100 and 250: increase by 100 miles (100, 200, 250)
      return Math.round(value / 100) * 100;
    }
    // Above 250 miles: increase by 250 miles (250, 500, 750, 1000, ...)
    return Math.round(value / 250) * 250;
  }
}

export default CustomScaleControl;
